import { Box, Typography } from "@mui/material";
import theme from "../../../../theme";
import { FullWidthSection } from "../../../ReusableComponents/FullWidthSection";
import { TaxonomyPath } from "../../../ReusableComponents/TaxonomyPath";
import {
  SPACING,
  BODY_SPACING,
} from "../../../../utils/constants/designConstants";
import { isDataEmpty } from "../../../../utils/functions";
import { CopyTextButton } from "../CopyButton";
import { AccreditationsTable } from "./Accreditations";
import { RegulationsTable } from "./Regulations";
import { SOMContentSection } from "./SizeOfMarket";
import { KCATSection } from "./KeyChallengesAndTrends/KCATSection";
import { SectionFallback } from "../../../../utils/error_message";
import { PFFReusableComponent } from "./PortersFiveForces/PFFReusableComponent";

const ContentBoxWithPadding = ({ children }) => (
  <div
    className="generate-insights-content-inside-box"
    style={{ padding: "2rem 0" }}
  >
    {children}
  </div>
);

export const MarketOverviewSection = ({
  taxonomyPath,
  newsAffectingPlans,
  sizeOfMarket,
  keyChallengesAndTrends,
  porterFiveForces,
  regulations,
  accreditations,
  showPorterFiveForces = true,
}) => {
  // Normalize data structure with fallbacks
  const newsAffectingPlansContent = newsAffectingPlans || {
    mainText: "",
    furtherReading: [],
  };
  const sizeOfMarketContent = sizeOfMarket || {
    sections: [],
    furtherReading: [],
  };
  const keyChallengesContent = keyChallengesAndTrends || {
    sections: [],
    furtherReading: [],
  };

  return (
    <>
      <FullWidthSection
        backgroundImage="/assets/newsAffectPlans.jpg"
        backgroundSize="cover"
        backgroundPosition="center"
      >
        <Box
          id="market-overview"
          component="section"
          aria-label="market overview"
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <ContentBoxWithPadding>
            <Box sx={{ my: SPACING.HEADING_TOP }}>
              <Typography variant="h1" component="h1">
                Market Overview
              </Typography>

              {/* Show taxonomy path */}
              <TaxonomyPath taxonomyPath={taxonomyPath} />
            </Box>

            {/* News Affecting Category Plans */}

            <ContentBoxWithPadding>
              <Box
                sx={{
                  gap: SPACING.SECTION_GAP,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {isDataEmpty(newsAffectingPlansContent?.mainText) ? (
                  <SectionFallback message="News affecting category plans are not available at the moment." />
                ) : (
                  <>
                    <Box
                      sx={{
                        borderRadius: theme.borderRadius.lg,
                      }}
                    >
                      <Typography variant="body1" sx={{ color: "text.primary" }}>
                        {newsAffectingPlansContent.mainText}
                      </Typography>
                      {newsAffectingPlansContent?.furtherReading?.length >
                        0 && (
                        <Box
                          sx={{
                            my: 2,
                            gap: 3,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {/* Further Reading */}
                          <Typography
                            variant="subtitle3"
                            component="subtitle3"
                            sx={{
                              color: "primary.brandSecondary",
                            }}
                          >
                            Further Reading
                          </Typography>
                          <Box
                            sx={{
                              columnCount: { sm: 1, md: 2 },
                              columnGap: 4,
                            }}
                          >
                            {newsAffectingPlansContent.furtherReading.map(
                              (item, index) => (
                                <Typography
                                  key={index}
                                  variant="body1"
                                  sx={{
                                    breakInside: "avoid",
                                    mb: SPACING.ITEM_GAP,
                                  }}
                                >
                                  {item}
                                </Typography>
                              ),
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </ContentBoxWithPadding>
          </ContentBoxWithPadding>
        </Box>
      </FullWidthSection>

      {/* Size of Market */}
      <FullWidthSection
        backgroundColor="primary.main"
        borderRadiusBottomRight={theme.borderRadius.full}
      >
        <ContentBoxWithPadding>
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box
              sx={{ display: "flex", position: "relative", alignItems: "center", mb: SPACING.SUBHEADING_BOTTOM }}
            >
              <Typography
                variant="h2"
                component="h2"
                sx={{ color: "primary.white", flexGrow: 1 }}
              >
                Size of Market
              </Typography>
              {(!isDataEmpty(sizeOfMarketContent?.sections) ||
                !isDataEmpty(sizeOfMarketContent?.furtherReading)) && (
                <CopyTextButton
                  textToCopy={sizeOfMarketContent}
                  sectionTitle="Size of Market"
                />
              )}
            </Box>

            {isDataEmpty(sizeOfMarketContent?.sections) &&
            isDataEmpty(sizeOfMarketContent?.furtherReading) ? (
              <Typography
                variant="body2"
                sx={{
                  color: "primary.white",
                  py: BODY_SPACING.ERROR_MESSAGE_SECTION_PADDING,
                  mb: BODY_SPACING.ERROR_MESSAGE_SECTION_MARGIN_BOTTOM,
                }}
              >
                Size of market is not available at the moment.
              </Typography>
            ) : (
              <>
                {sizeOfMarketContent?.sections?.length > 0 ? (
                  <SOMContentSection
                    sizeOfMarketContent={sizeOfMarketContent}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: "primary.white", whiteSpace: "pre-wrap" }}
                  >
                    {sizeOfMarket}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </ContentBoxWithPadding>
        {/* Image at bottom */}
        <Box
          component="img"
          src="/assets/SizeOfMarketImg.png"
          alt="Size of Market visual"
          sx={{
            width: "100%",
            maxHeight: { xs: 300, sm: 300, md: 400, xl: 500 },
            minHeight: { xs: 200, sm: 200, md: 300, xl: 400 },
            objectFit: "cover",
            objectPosition: "center",
            display: "block",
            mt: { xs: -24, sm: -24, md: -28, xl: -28 },
            borderBottomRightRadius: theme.borderRadius.full,
            position: "relative",
            zIndex: 0,
          }}
        />
      </FullWidthSection>

      {/* Key Challenges and Trends */}
      <FullWidthSection
        sx={{
          backgroundImage: `
            url('/assets/keyChallengesTop.png'),
            url('/assets/keyChallengesBottom.png')
            `,
          backgroundPosition: `top center, bottom center`,
          backgroundSize: `cover, cover`,
        }}
      >
        <ContentBoxWithPadding>
          <Box
            sx={{
              gap: SPACING.SECTION_GAP,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", position: "relative" }}
            >
              <Typography
                variant="h2"
                component="h2"
                sx={{ flex: 1, maxWidth: 500 }}
              >
                Key Challenges and Trends
              </Typography>
              {(!isDataEmpty(keyChallengesContent?.sections) ||
                !isDataEmpty(keyChallengesContent?.furtherReading)) && (
                <Box sx={{ position: "absolute", right: 0 }}>
                  <CopyTextButton
                    textToCopy={keyChallengesContent}
                    sectionTitle="Key Challenges and Trends"
                  />
                </Box>
              )}
            </Box>
            {isDataEmpty(keyChallengesContent?.sections) &&
            isDataEmpty(keyChallengesContent?.furtherReading) ? (
              <SectionFallback
                color="primary.white"
                message="Key challenges and trends information is not available at the moment."
              />
            ) : (
              <KCATSection keyChallengesContent={keyChallengesContent} />
            )}
          </Box>
        </ContentBoxWithPadding>
      </FullWidthSection>

      {/* Porter Five Forces */}
      {showPorterFiveForces && (
        <FullWidthSection
          backgroundColor="primary.main"
          borderRadiusBottomRight={theme.borderRadius.full}
        >
          <PFFReusableComponent porterFiveForces={porterFiveForces} />
        </FullWidthSection>
      )}

      {/* Regulations */}
      <FullWidthSection
        backgroundImage="/assets/reg.png"
        backgroundSize="cover"
        backgroundPosition="centerTop"
      >
        <ContentBoxWithPadding>
          <Typography
            variant="h2"
            component="h2"
            sx={{ color: "secondary.berry", mb: SPACING.SUBHEADING_BOTTOM }}
          >
            Regulations
          </Typography>
          <RegulationsTable regulations={regulations} />
        </ContentBoxWithPadding>
      </FullWidthSection>

      {/* Image */}
      <FullWidthSection
        backgroundImage="/assets/accRegImg.png"
        backgroundSize="cover"
        backgroundPosition="centerTop"
        sx={{
          maxHeight: { xs: 300, md: 500, xl: 800 },
          minHeight: { xs: 250, md: 400, xl: 700 },
        }}
        backgroundDisplay={{
          xs: "block",
          sm: "block",
          md: "block",
          lg: "block",
          xl: "block",
        }}
      />

      {/* Accreditations */}
      <FullWidthSection
        backgroundImage="/assets/acc.png"
        backgroundSize="cover"
        backgroundPosition="centerTop"
      >
        <ContentBoxWithPadding>
          <Typography
            variant="h2"
            component="h2"
            sx={{ color: "secondary.lawnGreen", mb: SPACING.SUBHEADING_BOTTOM }}
          >
            Accreditations
          </Typography>
          <AccreditationsTable accreditations={accreditations} />
        </ContentBoxWithPadding>
      </FullWidthSection>
    </>
  );
};
